// Geo-related utilities.
//
// A note on "Extent" and bounding boxes: several incompatible representations of a
// rectangular geographic area exist across this codebase, all conceptually the same
// thing (a west/south/east/north bounding box) but shaped differently:
//
// - `Extent` (this file): a plain `[west, south, east, north]` tuple. This is the format
//   used by most map code (e.g. RanaMap's `bounds` prop) and by the functions below.
// - `BoundingBox` (this file): a class with named `westmost`/`southmost`/`eastmost`/`northmost`
//   fields (kept as strings, since they often come from input fields or URLs) plus float
//   variants and helper methods (`toLeafletArray()`, `toGeometry()`, `containsPoint()`, etc.).
// - `@nens/threedi/openapi-client`'s `Extent`: a GeoJSON-*like* object
//   `{ type: string, coordinates: number[][] }`, used for e.g. `ThreediModel.extentZeroD`.
//   Despite the name, it is not interchangeable with the `Extent` tuple defined here.
// - GeoJSON `LineString`: used by some app-specific models (e.g. rana's own `ThreediModel`)
//   to represent the same bottom-left/top-right coordinate pair.
//
// Code that needs to combine or compare these values (e.g. `combineExtents` below) must first
// convert them to a common shape - usually the `Extent` tuple, since that's what most consuming
// map code expects.

import { bbox, bboxPolygon, buffer, distance, point } from "@turf/turf";
import { Feature, FeatureCollection, Geometry, Point, Polygon, Position } from "geojson";

import { getProjectionUnit } from "./projection";

export type Extent = [number, number, number, number] /* west south east north */;

export const NETHERLANDS_EXTENT: Extent = [3.358, 50.75, 7.227, 53.555];

export class BoundingBox {
  /* A generic Bounding Box class. Values are kept as strings because they often
  are in input fields, URLs, etc. */
  westmost: string;
  southmost: string;
  eastmost: string;
  northmost: string;
  westmostFloat: number;
  southmostFloat: number;
  eastmostFloat: number;
  northmostFloat: number;

  constructor(
    westmost: string | number,
    southmost: string | number,
    eastmost: string | number,
    northmost: string | number
  ) {
    this.westmost = "" + westmost;
    this.southmost = "" + southmost;
    this.eastmost = "" + eastmost;
    this.northmost = "" + northmost;
    this.westmostFloat = typeof westmost === "number" ? westmost : parseFloat(westmost);
    this.southmostFloat = typeof southmost === "number" ? southmost : parseFloat(southmost);
    this.eastmostFloat = typeof eastmost === "number" ? eastmost : parseFloat(eastmost);
    this.northmostFloat = typeof northmost === "number" ? northmost : parseFloat(northmost);
  }

  setField(field: string, value: string): BoundingBox {
    /* This class is immutable, setField returns a *new* BoundingBox. */
    const newBbox = { ...this, [field]: value };
    return new BoundingBox(
      newBbox.westmost,
      newBbox.southmost,
      newBbox.eastmost,
      newBbox.northmost
    );
  }

  toLeafletArray(): [[number, number], [number, number]] {
    /* Usage: new Leaflet.LatLngBounds(boundingBox.toLeafletArray()) */
    return [
      [this.southmostFloat, this.westmostFloat],
      [this.northmostFloat, this.eastmostFloat],
    ];
  }

  toLizardBbox() {
    /* For use in Lizard URLs. */
    return [this.westmost, this.southmost, this.eastmost, this.northmost].join(",");
  }

  toConfigBbox() {
    return [this.westmost, this.southmost, this.eastmost, this.northmost];
  }

  toGeometry() {
    return bboxPolygon([
      this.westmostFloat,
      this.southmostFloat,
      this.eastmostFloat,
      this.northmostFloat,
    ]);
  }

  containsPosition(point: Position) {
    const [longitude, latitude] = point;

    return (
      this.westmostFloat <= longitude &&
      longitude <= this.eastmostFloat &&
      this.southmostFloat <= latitude &&
      latitude <= this.northmostFloat
    );
  }

  containsPoint(point?: Geometry | null) {
    return point && point.type === "Point" && this.containsPosition(point.coordinates);
  }

  containsGeometry(geometry?: Geometry | null): boolean {
    if (geometry === null || geometry === undefined) return false;

    if (geometry.type === "Point") {
      return this.containsPosition(geometry.coordinates);
    }

    if (geometry.type === "LineString") {
      return geometry.coordinates.every((position) => this.containsPosition(position));
    }

    if (geometry.type === "Polygon") {
      return geometry.coordinates.every((subpolygon) =>
        subpolygon.every((position) => this.containsPosition(position))
      );
    }

    if (geometry.type === "MultiPolygon") {
      return geometry.coordinates.every((polygon) =>
        polygon.every((subpolygon) =>
          subpolygon.every((position) => this.containsPosition(position))
        )
      );
    }

    return false;
  }
}

export function isSamePoint(a: Geometry, b: Geometry, epsilon = 0) {
  return (
    a.type === "Point" &&
    b.type === "Point" &&
    a.coordinates &&
    a.coordinates.length >= 2 &&
    b.coordinates &&
    b.coordinates.length >= 2 &&
    Math.abs(a.coordinates[0] - b.coordinates[0]) <= epsilon &&
    Math.abs(a.coordinates[1] - b.coordinates[1]) <= epsilon
  );
}

export function pointInPolygon(point: Point, polygon: Polygon) {
  // ray-casting algorithm based on
  // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html
  // Shamelessly stolen and adapted from https://github.com/substack/point-in-polygon
  if (!point) return false;

  const x = point.coordinates[0];
  const y = point.coordinates[1];

  let inside = false;

  const vs = polygon.coordinates[0]; // We only use the outer boundary, ignore the holes

  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    const xi = vs[i][0],
      yi = vs[i][1];
    const xj = vs[j][0],
      yj = vs[j][1];

    const yiGreaterY = yi > y;
    const yjGreaterY = yj > y;
    const intersect = yiGreaterY !== yjGreaterY && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
}

export function isFeature(json: unknown): json is Feature {
  if (typeof json !== "object" || json === null) return false;
  if ((json as Record<string, unknown>)["type"] !== "Feature") return false;
  const j = json as Record<string, unknown>;
  if (!("type" in j) || j["type"] !== "Feature") return false;
  const geometry = (json as Record<string, unknown>)["geometry"];
  if (!geometry) return false;
  return true;
}

export function isFeatureCollection(json: unknown): json is FeatureCollection {
  if (typeof json !== "object" || json === null) return false;
  if ((json as Record<string, unknown>)["type"] !== "FeatureCollection") return false;
  const features = (json as Record<string, unknown>)["features"];
  if (!features || !Array.isArray(features)) return false;
  return features.every(isFeature);
}

export function validateGeometry(geometry: Geometry, errorFn: (error: string) => void) {
  if (geometry.type === "Point") {
    isPosition(geometry.coordinates, errorFn);
  } else if (geometry.type === "LineString") {
    isLineString(geometry.coordinates, errorFn);
  } else if (geometry.type === "Polygon") {
    isPolygon(geometry.coordinates, errorFn);
  } else {
    errorFn(`Feature geometry type must be Point, LineString or Polygon, not "${geometry.type}".`);
  }
}

export function isPosition(position: Position, errorFn: (error: string) => void): boolean {
  if (!Array.isArray(position)) {
    errorFn("Position coordinates must be an array.");
    return false;
  }
  if (position.length < 2 || position.length > 3) {
    errorFn("Position coordinates must be an array with two or three elements.");
    return false;
  }
  if (typeof position[0] !== "number" || typeof position[1] !== "number") {
    errorFn("Coordinates must be numbers.");
    return false;
  }
  return true;
}

export function isLineString(lineString: Position[], errorFn: (error: string) => void): boolean {
  if (!Array.isArray(lineString)) {
    errorFn("LineString coordinates must be an array.");
    return false;
  }
  if (lineString.length < 2) {
    errorFn("LineString coordinates must have at least two positions.");
    return false;
  }
  return lineString.every((position) => isPosition(position, errorFn));
}

export function isLinearRing(lineString: Position[], errorFn: (error: string) => void): boolean {
  if (!isLineString(lineString, errorFn)) return false;

  const length = lineString.length;

  if (length < 4) {
    errorFn("Polygon element must have at least four positions.");
    return false;
  }
  if (
    lineString[0][0] !== lineString[length - 1][0] ||
    lineString[0][1] !== lineString[length - 1][1]
  ) {
    errorFn("First and last position of Polygon element must be the same.");
    return false;
  }
  return true;
}

export function isPolygon(polygon: Position[][], errorFn: (error: string) => void): boolean {
  if (!Array.isArray(polygon) || polygon.length === 0) {
    errorFn("Polygon must be an array of one or more LineStrings.");
    return false;
  }
  return polygon.every((linearRing) => isLinearRing(linearRing, errorFn));
}

export function convertLizardBoundsToMapExtent(
  bounds: {
    west: number;
    south: number;
    east: number;
    north: number;
  } | null
): Extent | null {
  if (!bounds) return null;

  const { west, south, east, north } = bounds;
  return [west, south, east, north];
}

export function convertGeometryToMapExtent(geometry: Geometry | null) {
  if (!geometry) return null;

  if (geometry.type === "Point") {
    // Create a buffer zone around the point
    const bufferDistance = 200;
    const bufferedPoint = buffer(geometry, bufferDistance, { units: "meters" });
    if (bufferedPoint !== undefined) {
      // It should never be undefined, as a fallback the normal return below exists
      return bbox(bufferedPoint) as Extent;
    }
  }

  return bbox(geometry) as Extent;
}

function toExtent(extent: Extent | BoundingBox): Extent {
  return extent instanceof BoundingBox
    ? [extent.westmostFloat, extent.southmostFloat, extent.eastmostFloat, extent.northmostFloat]
    : extent;
}

// Combines any number of extents into the smallest extent containing all of them.
export function combineExtents(extents: (Extent | BoundingBox)[]): Extent | null {
  if (extents.length === 0) return null;

  // Seed the accumulator with the first extent's own bounds (re-visited in the loop below)
  // rather than +/-Infinity, since that would need extra handling to strip out again.
  let [minLng, minLat, maxLng, maxLat] = toExtent(extents[0]);

  extents.forEach((extent) => {
    const [minLng2, minLat2, maxLng2, maxLat2] = toExtent(extent);
    minLng = Math.min(minLng, minLng2);
    minLat = Math.min(minLat, minLat2);
    maxLng = Math.max(maxLng, maxLng2);
    maxLat = Math.max(maxLat, maxLat2);
  });

  return [minLng, minLat, maxLng, maxLat];
}

const LOWEST_LIZARD_RASTER_RESOLUTION = 0.1; // pixel
const DEFAULT_PIXEL_SIZE = 256;

export function getGeometryPixelSize(
  geometry: Geometry,
  pixel_size?: number,
  projection?: string | null
) {
  const geometryBbox = bbox(geometry);
  const pixelSize = pixel_size ?? DEFAULT_PIXEL_SIZE;
  const { units } = getProjectionUnit(projection ?? null);

  // width and height are in pixels (meters / pixelSize)
  const height =
    distance(point([geometryBbox[0], geometryBbox[1]]), point([geometryBbox[0], geometryBbox[3]]), {
      units,
    }) / pixelSize;

  const width =
    distance(point([geometryBbox[0], geometryBbox[1]]), point([geometryBbox[2], geometryBbox[1]]), {
      units,
    }) / pixelSize;

  const maxPixelSize = Math.max(LOWEST_LIZARD_RASTER_RESOLUTION, height, width);

  return {
    height: Math.round(height),
    width: Math.round(width),
    areaPixels: Math.round(height * width),
    maxPixelSize,
  };
}

// Haversine formula
export const getDistanceInM = (from: Position, to: Position) => {
  const R = 6371000; // Radius of the earth in m
  const [lon1, lat1] = from;
  const [lon2, lat2] = to;

  const dLat = deg2rad(lat2 - lat1); // deg2rad below
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in m
};

const deg2rad = (deg: number) => {
  return deg * (Math.PI / 180);
};
